import { withSpan } from "../observability/tracing";
import { ForensicLogger } from "../observability/ForensicLogger";
import { telemetry } from "../../../wingman-miami-spatial-04092026-main/src/spatial/utils/telemetry";

// Inline camera profile stub
const CAMERA_PROFILES: Record<string, { zoom: number; pitch: number; bearing: number; speed: number; curve: number }> = {
    overview:   { zoom: 13,   pitch: 45, bearing: 0,   speed: 1.0, curve: 1.42 },
    detail:     { zoom: 17,   pitch: 60, bearing: -20, speed: 1.2, curve: 1.42 },
    street:     { zoom: 18.5, pitch: 72, bearing: -30, speed: 0.8, curve: 1.2  },
    aerial:     { zoom: 14,   pitch: 50, bearing: 10,  speed: 1.0, curve: 1.42 },
    default:    { zoom: 15.5, pitch: 60, bearing: -20, speed: 1.2, curve: 1.42 },
};

function getCameraProfile(intent: string) {
    return CAMERA_PROFILES[intent] || CAMERA_PROFILES.default;
}

declare global {
    interface Window {
        WingmanSpatialControl?: any;
        WingmanStateMachine?: any;
        WingmanTraceRecorder?: any;
        WingmanEventBus?: any;
        WingmanCommandController?: any;
        NavigationController?: any;
        contextState?: any;
    }
}

export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
}

type Listener = (data: any) => void;

class SpatialEventBus {
    private listeners: Record<string, Listener[]> = {};
    private emitStack: string[] = [];
    private cameraQueue: any[] = [];
    private isProcessingQueue = false;
    private cameraLock = { active: false, source: null as string | null, timestamp: 0, releaseTimer: null as any };
    private navigationDebounce = { lastLat: 0, lastLon: 0, lastTimestamp: 0 };
    private lastMarkerHash: string | null = null;
    private currentNavTelemetry: any = null;
    
    // Public state for backward compatibility
    public viewportState = {
        padding: { top: 100, bottom: 100, left: 100, right: 400 },
        screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
        sidebarWidth: 400
    };

    constructor() {
        // [FORENSIC][EVENT_BUS][INIT]
        if (import.meta.env.VITE_ENABLE_VERBOSE_LOGS === 'true') {
            console.info('[SpatialEventBus] 🧬 Maverick Hub Initialized');
        }
    }

    // ============================================================================
    // CAMERA LOCK SYSTEM
    // ============================================================================

    /**
     * Request a camera lock for a given source.
     * Priority: USER > COPILOT > SYNC > AI
     * 
     * Lock rules:
     * - USER always wins (highest priority)
     * - Same source can re-lock
     * - Only USER can override AI lock (AI is sticky against COPILOT/SYNC)
     * - COPILOT can override SYNC and vice versa (same tier)
     */
    public requestLock(source: string | null): boolean {
        if (!source) return false;

        const LOCK_PRIORITY: Record<string, number> = {
            'USER': 5.1,
            'VOICE': 5.1,
            'REMOTE_USER': 5.0,
            'COPILOT': 4,
            'SYNC': 3,
            'AI': 2
        };

        const incomingPriority = LOCK_PRIORITY[source] || 0;
        const currentSource = this.cameraLock.source;
        const currentPriority = currentSource ? (LOCK_PRIORITY[currentSource] || 0) : 0;

        // Always allow if no lock active
        if (!this.cameraLock.active) {
            if (source === 'USER') {
                this.forceLock('USER');
            } else {
                this.cameraLock.active = true;
                this.cameraLock.source = source;
                this.cameraLock.timestamp = Date.now();
            }
            return true;
        }

        // USER always wins
        if (source === 'USER') {
            this.forceLock('USER');
            return true;
        }

        // 🛡️ SYNC COOLDOWN: Block non-USER commands for 500ms after a USER move
        // This prevents the sidepanel map from "fighting back" while the user is dragging the main map.
        const isRecentUserMove = currentSource === 'USER' && (Date.now() - this.cameraLock.timestamp < 500);
        if (isRecentUserMove && source !== 'USER') {
            telemetry.warn('EVENT_BUS', 'SYNC_LOCK_BLOCKED', { source, currentSource });
            return false;
        }

        // Same source can re-lock
        if (source === currentSource) {
            this.cameraLock.timestamp = Date.now();
            return true;
        }

        // AI is sticky against SYNC/lower: only USER and VOICE can override AI lock
        if (currentSource === 'AI' && (source !== 'USER' && source !== 'VOICE')) {
            return false;
        }

        // Higher priority overrides lower (but not AI - handled above)
        if (incomingPriority > currentPriority) {
            this.forceLock(source);
            return true;
        }

        return false;
    }

    /**
     * Force a lock for a given source, clearing any existing lock.
     */
    public forceLock(source: string): void {
        if (this.cameraLock.releaseTimer) {
            clearTimeout(this.cameraLock.releaseTimer);
            this.cameraLock.releaseTimer = null;
        }

        this.cameraLock.active = true;
        this.cameraLock.source = source;
        this.cameraLock.timestamp = Date.now();

        // Auto-release USER locks after 5s
        if (source === 'USER') {
            this.cameraLock.releaseTimer = setTimeout(() => {
                this.cameraLock.active = false;
                this.cameraLock.source = null;
            }, 5000);
        }
    }

    /**
     * Release the camera lock, optionally after a delay.
     */
    public releaseLock(delay: number = 0): void {
        if (delay > 0) {
            if (this.cameraLock.releaseTimer) {
                clearTimeout(this.cameraLock.releaseTimer);
            }
            this.cameraLock.releaseTimer = setTimeout(() => {
                this.cameraLock.active = false;
                this.cameraLock.source = null;
                this.cameraLock.releaseTimer = null;
            }, delay);
        } else {
            if (this.cameraLock.releaseTimer) {
                clearTimeout(this.cameraLock.releaseTimer);
                this.cameraLock.releaseTimer = null;
            }
            this.cameraLock.active = false;
            this.cameraLock.source = null;
        }
    }

    public getLockOwner(): string | null {
        return this.cameraLock.active ? this.cameraLock.source : null;
    }

    public isLocked(): boolean {
        return this.cameraLock.active;
    }

    /**
     * ATOM W-010: Force release all locks regardless of source.
     * Used during purgeAll() and cold boot recovery.
     */
    public forceReleaseAllLocks(): void {
        telemetry.warn('EVENT_BUS', 'FORCE_RELEASE_ALL_LOCKS');
        if (this.cameraLock.releaseTimer) {
            clearTimeout(this.cameraLock.releaseTimer);
            this.cameraLock.releaseTimer = null;
        }
        this.cameraLock.active = false;
        this.cameraLock.source = null;
        this.cameraLock.timestamp = 0;
    }

    /**
     * ATOM W-010: Get all active locks with metadata.
     * Returns an array of lock objects for forensic inspection.
     */
    public getActiveLocks(): Array<{ source: string | null; active: boolean; timestamp: number }> {
        return [{
            source: this.cameraLock.source,
            active: this.cameraLock.active,
            timestamp: this.cameraLock.timestamp
        }];
    }

    // 📡 SIGNAL CORE
    public on(event: string, cb: Listener) {
        if (!this.listeners[event]) this.listeners[event] = [];
        
        // 🛡️ DUPLICATE PREVENTION
        if (this.listeners[event].includes(cb)) {
            return () => this.off(event, cb);
        }
        
        this.listeners[event].push(cb);
        
        // 🛡️ UNSUBSCRIBE RETURN PATTERN
        return () => this.off(event, cb);
    }

    public off(event: string, cb: Listener) {
        if (!this.listeners[event]) return;
        this.listeners[event] = this.listeners[event].filter(f => f !== cb);
    }

    public emit(event: string, data: any) {
        // 🛡️ RECURSION GUARD: Prevent amplification loops
        if (this.emitStack.includes(event)) {
            telemetry.error('EVENT_BUS', 'RECURSION_BLOCKED', { 
                event, 
                stack: [...this.emitStack],
                depth: this.emitStack.length
            });
            if (typeof window !== 'undefined' && (window as any).WingmanTraceRecorder) {
                (window as any).WingmanTraceRecorder.record('RECURSION_BLOCKED', { event });
            }
            return;
        }
        
        this.emitStack.push(event);
        
        try {
            const emitId = `emit_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
            const listenerCount = this.listeners[event]?.length || 0;
            
            // Standard log without async telemetry import to prevent timing drifts during emit
            console.debug(`[EventBus] EMIT: ${event} (${listenerCount} listeners)`);

            if (event === 'SPATIAL_INTENT') {
                console.log('[SPATIAL] Intent received');
                console.log('[SPATIAL] Intent payload', data);
            }

            if (event === 'SPATIAL_COMMAND' || event === 'SPATIAL_INTENT') {
                ForensicLogger.log('spatial_signal', { event, data, emitId });
            }
            
            if (!this.listeners[event]) return;
            this.listeners[event].forEach(cb => {
                try {
                    cb(data);
                } catch (err) {
                    console.error(`[EventBus] ❌ Listener failed for ${event}:`, err);
                }
            });
        } finally {
            this.emitStack.pop();
        }
    }

    /**
     * ATOM S1.5 — Cold Boot Purge
     * Deterministically clear all listeners, locks, and stale queues.
     */
    public purgeAll() {
        if (this.cameraLock.releaseTimer) {
            clearTimeout(this.cameraLock.releaseTimer);
        }
        this.listeners = {};
        this.emitStack = [];
        this.cameraQueue = [];
        this.isProcessingQueue = false;
        this.cameraLock = { active: false, source: null, timestamp: 0, releaseTimer: null };
        this.navigationDebounce = { lastLat: 0, lastLon: 0, lastTimestamp: 0 };
        this.lastMarkerHash = null;
        console.warn('[EventBus] 🛡️ Cold Boot: All listeners and locks purged');
    }

    /** @deprecated Use purgeAll() */
    public clear() {
        this.purgeAll();
    }

    /**
     * Legacy dispatch bridge.
     * 🛡️ ATOM S9.0.6: Wraps legacy commands into the standardized intent pipeline.
     */
    public dispatch(command: any) {
        telemetry.forensic('INTENT', 'RECEIVED', {
            source: command.source || 'LEGACY',
            intentType: command.action || 'MOVE',
            authority: command.source || 'AI',
            correlationId: command.correlationId || 'none'
        });

        // Forward to the authoritative SpatialBridge
        this.emit('SPATIAL_INTENT', {
            intentId: `legacy_${Date.now()}`,
            intentType: command.action === 'CAMERA_MOVE' ? 'NAVIGATE' : (command.action === 'CAMERA_FIT_BOUNDS' ? 'FIT_BOUNDS' : 'MOVE'),
            source: command.source || 'AI',
            payload: command.params,
            timestamp: Date.now()
        });
    }

    // 🛡️ UTILITIES (Maintained for legacy controllers)
    
    public shouldUpdateMarkers(markers: any[]) {
        if (!markers || markers.length === 0) {
             if (this.lastMarkerHash) { this.lastMarkerHash = null; return true; }
             return false;
        }
        const hash = markers.map((m: any) => `${m.id || m.name || ''}-${m.lat}-${m.lon || m.lng}`).join('|');
        if (hash === this.lastMarkerHash) return false;
        this.lastMarkerHash = hash;
        return true;
    }

    public resolveCenterFromParams(params: any) {
        if (!params) return null;
        if (params.center && Array.isArray(params.center) && params.center.length === 2) {
            // Validate that coordinates are finite numbers
            if (isFinite(params.center[0]) && isFinite(params.center[1])) {
                return params.center;
            }
            return null;
        }
        if (params.bounds) {
            // Simplified bounds center calculation
            const b = params.bounds;
            if (Array.isArray(b) && b.length === 2 && Array.isArray(b[0]) && Array.isArray(b[1])) {
                const lon = (b[0][0] + b[1][0]) / 2;
                const lat = (b[0][1] + b[1][1]) / 2;
                if (isFinite(lon) && isFinite(lat)) {
                    return [lon, lat];
                }
            }
        }
        return null;
    }
}

// 🚀 SINGLETON ENFORCEMENT
// We must ensure only ONE EventBus exists across all bundles.
const globalBus = (typeof window !== 'undefined' && window.WingmanEventBus) 
    ? window.WingmanEventBus 
    : new SpatialEventBus();

if (typeof window !== 'undefined') {
    window.WingmanEventBus = globalBus;
}

export const spatialEventBus = globalBus;
